import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Chip, Grid,
  Stepper, Step, StepLabel, StepContent, Snackbar, Alert, Tooltip,
  MenuItem, Paper,
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  LocalShipping as TrackingIcon, Close as CloseIcon, Save as SaveIcon,
  AddCircleOutline as AddStepIcon, RemoveCircleOutline as RemoveStepIcon,
} from "@mui/icons-material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { apiList, invokeApi, invokeGetApi, invokeDeleteApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";

const STATUS_OPTIONS = ["Order Placed", "Processing", "Shipped", "Out for Delivery", "Delivered", "Cancelled", "Returned"];
const STATUS_COLOR = {
  "Order Placed": "#1E40AF", "Processing": "#D97706", "Shipped": "#0284C7",
  "Out for Delivery": "#7C3AED", "Delivered": "#2E7D32", "Cancelled": "#C62828", "Returned": "#6B7280",
};

const DEMO_TRACKING = [
  {
    id: 1, order_number: "ORD-2026-1001", customer_name: "Rahul Sharma", customer_phone: "9876543210", current_status: "Delivered",
    steps: [
      { status: "Order Placed", location: "Bangalore", description: "Order received and confirmed.", timestamp: "2026-09-30T10:00" },
      { status: "Processing", location: "Bangalore Hub", description: "Product activated and license issued.", timestamp: "2026-09-30T12:00" },
      { status: "Delivered", location: "Customer Email", description: "License key delivered to customer email.", timestamp: "2026-09-30T14:00" },
    ],
  },
  {
    id: 2, order_number: "ORD-2026-1002", customer_name: "Anita Verma", customer_phone: "9123456789", current_status: "Processing",
    steps: [
      { status: "Order Placed", location: "Hyderabad", description: "Order received.", timestamp: "2026-09-30T11:30" },
      { status: "Processing", location: "Hyderabad Hub", description: "License activation in progress.", timestamp: "2026-09-30T13:00" },
    ],
  },
];

const emptyStep = { status: "Order Placed", location: "", description: "", timestamp: "" };
const emptyForm = { order_number: "", customer_name: "", customer_phone: "", current_status: "Order Placed", steps: [{ ...emptyStep }] };

const TrackingManager = () => {
  const [cookies] = useCookies();
  const [trackingList, setTrackingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [viewDialog, setViewDialog] = useState(null);

  useEffect(() => { fetchTracking(); }, []);

  const fetchTracking = async () => {
    setLoading(true);
    try {
      const res = await invokeGetApi(config.apiUrl + "/getAllOrderTrack", {});
      if (res?.status === 200 && res?.data?.tracks?.length > 0) {
        setTrackingList(res.data.tracks);
      } else { setTrackingList(DEMO_TRACKING); }
    } catch { setTrackingList(DEMO_TRACKING); }
    finally { setLoading(false); }
  };

  const handleOpenAdd = () => { setEditId(null); setForm({ ...emptyForm, steps: [{ ...emptyStep }] }); setDialogOpen(true); };
  const handleOpenEdit = (track) => {
    setEditId(track.id);
    setForm({ order_number: track.order_number, customer_name: track.customer_name, customer_phone: track.customer_phone,
      current_status: track.current_status, steps: track.steps?.length > 0 ? track.steps.map(s => ({ ...s })) : [{ ...emptyStep }] });
    setDialogOpen(true);
  };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const handleStepChange = (index, field, value) => {
    setForm(prev => { const steps = [...prev.steps]; steps[index] = { ...steps[index], [field]: value }; return { ...prev, steps }; });
  };
  const addStep = () => setForm(prev => ({ ...prev, steps: [...prev.steps, { ...emptyStep }] }));
  const removeStep = (index) => setForm(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }));

  const handleSave = async () => {
    if (!form.order_number.trim() || !form.customer_name.trim()) {
      setSnackbar({ open: true, message: "Order number and customer name are required.", severity: "warning" }); return;
    }
    setSaving(true);
    try {
      const endpoint = editId ? config.apiUrl + `/updateOrderTrack/${editId}` : config.apiUrl + "/addOrderTrack";
      const res = await invokeApi(endpoint, form, cookies);
      if (res?.status === 200) {
        setSnackbar({ open: true, message: editId ? "Tracking updated!" : "Tracking created!", severity: "success" });
      } else {
        if (editId) setTrackingList(prev => prev.map(t => t.id === editId ? { ...t, ...form } : t));
        else setTrackingList(prev => [{ id: Date.now(), ...form }, ...prev]);
        setSnackbar({ open: true, message: editId ? "Tracking updated (local)." : "Tracking added (local).", severity: "info" });
      }
      setDialogOpen(false);
    } catch { setSnackbar({ open: true, message: "Error saving tracking.", severity: "error" }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await invokeDeleteApi(config.apiUrl + `/deleteOrderTrack/${id}`);
      setTrackingList(prev => prev.filter(t => t.id !== id));
      setSnackbar({ open: true, message: "Tracking deleted.", severity: "success" });
    } catch { setTrackingList(prev => prev.filter(t => t.id !== id)); }
    finally { setDeleteConfirm(null); }
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#F9FAF4", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Header />
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TrackingIcon sx={{ fontSize: 36, color: "#1E40AF" }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#1E40AF" }}>Order Tracking Manager</Typography>
                <Typography variant="body2" color="text.secondary">Add / update tracking info — customers see this on the Track Order page</Typography>
              </Box>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}
              sx={{ bgcolor: "#1E40AF", borderRadius: 0, "&:hover": { bgcolor: "#1E3A8A" } }}>
              ADD TRACKING
            </Button>
          </Box>

          <Card elevation={0} sx={{ border: "1px solid #EAEAEA", borderRadius: 0 }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#F1F3F4" }}>
                  <TableRow>
                    {["#", "ORDER NUMBER", "CUSTOMER", "PHONE", "CURRENT STATUS", "STEPS", "ACTIONS"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700 }} align={h === "ACTIONS" ? "right" : "left"}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 10 }}><CircularProgress sx={{ color: "#1E40AF" }} /></TableCell></TableRow>
                  ) : trackingList.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: "#666" }}>No tracking entries found. Click "ADD TRACKING" to create one.</TableCell></TableRow>
                  ) : trackingList.map((track, idx) => (
                    <TableRow key={track.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#1E40AF" }}>{track.order_number}</TableCell>
                      <TableCell>{track.customer_name}</TableCell>
                      <TableCell>{track.customer_phone}</TableCell>
                      <TableCell>
                        <Chip label={track.current_status} size="small"
                          sx={{ bgcolor: `${STATUS_COLOR[track.current_status] || "#6B7280"}20`, color: STATUS_COLOR[track.current_status] || "#6B7280", fontWeight: 700, borderRadius: 1 }} />
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => setViewDialog(track)}
                          sx={{ borderColor: "#1E40AF", color: "#1E40AF", borderRadius: 0 }}>
                          View {track.steps?.length || 0} Steps
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small" sx={{ color: "#1E40AF" }} onClick={() => handleOpenEdit(track)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" sx={{ color: "#C62828" }} onClick={() => setDeleteConfirm(track.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Paper elevation={0} sx={{ mt: 3, p: 2.5, border: "1px solid #DBEAFE", bgcolor: "#EFF6FF", borderRadius: 2 }}>
            <Typography variant="body2" sx={{ color: "#1E40AF", fontWeight: 600 }}>💡 How Tracking Works</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              When a customer goes to <strong>/track-order</strong> and enters their order number or phone, they see the tracking steps you define here in real time.
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1E40AF", borderBottom: "1px solid #EEE", display: "flex", justifyContent: "space-between" }}>
          {editId ? "Edit Order Tracking" : "Add New Tracking Entry"}
          <IconButton onClick={() => setDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><TextField fullWidth label="Order Number *" size="small" value={form.order_number} onChange={e => handleFormChange("order_number", e.target.value)} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Customer Name *" size="small" value={form.customer_name} onChange={e => handleFormChange("customer_name", e.target.value)} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Customer Phone" size="small" value={form.customer_phone} onChange={e => handleFormChange("customer_phone", e.target.value)} /></Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth select label="Current Status *" size="small" value={form.current_status} onChange={e => handleFormChange("current_status", e.target.value)}>
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1E40AF" }}>Tracking Steps (shown to customer)</Typography>
              <Button startIcon={<AddStepIcon />} onClick={addStep} size="small" sx={{ color: "#1E40AF" }}>Add Step</Button>
            </Box>
            {form.steps.map((step, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>STEP {index + 1}</Typography>
                  {form.steps.length > 1 && <IconButton size="small" sx={{ color: "#C62828" }} onClick={() => removeStep(index)}><RemoveStepIcon fontSize="small" /></IconButton>}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth select label="Status" size="small" value={step.status} onChange={e => handleStepChange(index, "status", e.target.value)}>
                      {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}><TextField fullWidth label="Location" size="small" value={step.location} onChange={e => handleStepChange(index, "location", e.target.value)} placeholder="e.g. Bangalore Hub" /></Grid>
                  <Grid item xs={12} md={3}><TextField fullWidth label="Timestamp" size="small" value={step.timestamp} onChange={e => handleStepChange(index, "timestamp", e.target.value)} type="datetime-local" InputLabelProps={{ shrink: true }} /></Grid>
                  <Grid item xs={12} md={3}><TextField fullWidth label="Description" size="small" value={step.description} onChange={e => handleStepChange(index, "description", e.target.value)} placeholder="e.g. License key sent to email" /></Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #EEE" }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ bgcolor: "#1E40AF", borderRadius: 0, "&:hover": { bgcolor: "#1E3A8A" } }}>
            {saving ? "Saving..." : editId ? "UPDATE TRACKING" : "CREATE TRACKING"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Steps Dialog */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="sm" fullWidth>
        {viewDialog && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: "#1E40AF", borderBottom: "1px solid #EEE" }}>
              Tracking Steps — {viewDialog.order_number}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Stepper orientation="vertical">
                {viewDialog.steps?.map((step, i) => (
                  <Step key={i} active completed={i < viewDialog.steps.length - 1}>
                    <StepLabel StepIconProps={{ style: { color: STATUS_COLOR[step.status] || "#1E40AF" } }}>
                      <Typography sx={{ fontWeight: 700, color: STATUS_COLOR[step.status] || "#1E40AF" }}>{step.status}</Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">{step.location}</Typography>
                      <Typography variant="body2">{step.description}</Typography>
                      <Typography variant="caption" color="text.secondary">{step.timestamp}</Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewDialog(null)} variant="contained" sx={{ bgcolor: "#1E40AF", borderRadius: 0 }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Tracking?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently remove this tracking entry.</Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => setDeleteConfirm(null)} variant="outlined">Cancel</Button>
            <Button onClick={() => handleDelete(deleteConfirm)} variant="contained" sx={{ bgcolor: "#C62828", "&:hover": { bgcolor: "#8e0000" } }}>Delete</Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 0 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TrackingManager;
